import React, { useState, useEffect } from 'react'
import { useDataProvider } from 'react-admin'
import { Box, Typography, CircularProgress } from '@mui/material'

const CustomAudioField = ({
  label,
  signedUrl: propSignedUrl,
  loading: propLoading = false,
  audioPath,
  inline = false,
  ...props
}) => {
  const dataProvider = useDataProvider()
  const [internalSignedUrl, setInternalSignedUrl] = useState('')
  const [internalLoading, setInternalLoading] = useState(false)

  // Use prop values if provided, otherwise use internal state
  const signedUrl = propSignedUrl || internalSignedUrl
  const loading = propLoading || internalLoading

  // Function to get signed URL for audio file
  const getSignedUrl = async audioPath => {
    if (!audioPath) return ''

    try {
      setInternalLoading(true)
      const response = await dataProvider.create('ai-books/get-signed-url', {
        data: { audioPath },
      })
      return response.data.signedUrl || ''
    } catch (error) {
      console.error('Error getting signed URL:', error)
      return ''
    } finally {
      setInternalLoading(false)
    }
  }

  // Fetch signed URL when audioPath changes and no signedUrl is provided
  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (audioPath && !propSignedUrl) {
        const url = await getSignedUrl(audioPath)
        setInternalSignedUrl(url)
      } else if (!audioPath) {
        setInternalSignedUrl('')
      }
    }
    fetchSignedUrl()
  }, [audioPath, propSignedUrl])

  // If no audio path is provided, don't render anything
  if (!audioPath) {
    return null
  }

  const containerStyle = inline
    ? {}
    : { mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }

  return (
    <Box sx={containerStyle}>
      <Typography variant="subtitle2" gutterBottom>
        {label} {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
      </Typography>
      {signedUrl && !loading ? (
        <audio controls style={{ width: '100%' }} preload="metadata">
          <source src={signedUrl} type="audio/mpeg" />
          <source src={signedUrl} type="audio/wav" />
          <source src={signedUrl} type="audio/ogg" />
          Your browser does not support the audio element.
        </audio>
      ) : loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Audio not available
        </Typography>
      )}
    </Box>
  )
}

export default CustomAudioField
